import { useState, useCallback, useRef, useEffect } from "react";
import {
  APPLICATION_ID,
  AUTH_BASE_URL,
  CLIENT_ID,
  REDIRECT_URI,
} from "../Config/Config";
import {
  generateCodeChallenge,
  generateCodeVerifier,
} from "../utils/PkceUtils";
import { useDispatch } from "react-redux";
import * as jose from "jose";
import { DateTime } from "luxon";
import { setOAuthTokens } from "../features/Loginuser";
import { clearPkceFlowData, getPkceFlowData, storePkceFlowData } from "../utils/TokenManager";

export function useSilentAuth() {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const iframeRef = useRef(null);

  const cleanup = useCallback(() => {
    if (iframeRef.current && iframeRef.current.parentNode) {
      iframeRef.current.parentNode.removeChild(iframeRef.current);
      iframeRef.current = null;
    }
    clearPkceFlowData();
    setLoading(false);
  }, []);

  const handleMessage = useCallback(
    async (event) => {
      if (event.origin !== window.location.origin) return;

      if (event.data.type === "SILENT_AUTH_SUCCESS") {
        const authCode = event.data.code;
        const state = event.data.state;
        const pkceData = getPkceFlowData();

        if (!pkceData) {
          cleanup();
          return;
        }

        if (state !== pkceData.state) {
          cleanup();
          return;
        }

        try {
          const response = await fetch(`${AUTH_BASE_URL}/oauth2/token`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: new URLSearchParams({
              client_id: CLIENT_ID,
              grant_type: "authorization_code",
              code: authCode,
              redirect_uri: REDIRECT_URI,
              code_verifier: pkceData.codeVerifier,
            }),
          });

          const data = await response.json();
          if (data.access_token) {
            setToken(data.access_token);
          }
          const decodedClaims = jose.decodeJwt(data.access_token);
          const decodedIdClaims = jose.decodeJwt(data.id_token);
          const userDetail = {
            // You can decode the JWT token here to get user info
            // or fetch user info from a /userinfo endpoint
            authenticated: true,
            user_id: decodedClaims.user_id,
            user_roles: decodedClaims.user_roles || [],
            username: decodedClaims.sub,
          };

          const loginTime = localStorage.getItem("loginTime");
          if (loginTime) {
            userDetail.loginTime = loginTime;
          }
         
          // Dispatch OAuth tokens to Redux
          dispatch(
            setOAuthTokens({
              login:true,
              accessToken: data.access_token,
              tokenReady: true,
              idToken: data.id_token,
              accessTokenExpiresAt: DateTime.fromSeconds(
                decodedClaims.exp,
              ).toISO(),
              accessTokenExpiresIn: data.expires_in,
              userDetail: userDetail,
            }),
          );
          scheduleTokenRefresh(decodedClaims.exp);
        } catch (err) {
        } finally {
          cleanup();
        }
      } else if (event.data.type === "SILENT_AUTH_ERROR") {
        cleanup();
      }
    },
    [cleanup],
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [handleMessage]);

  const triggerSilentFlow = useCallback(async () => {
    setLoading(true);
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const state = generateCodeVerifier();

    const pkceData = {
      codeVerifier,
      state,
      timestamp: Date.now(),
    };

    storePkceFlowData(pkceData);

    if (iframeRef.current && iframeRef.current.parentNode) {
      iframeRef.current.parentNode.removeChild(iframeRef.current);
    }

    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src =
      `${AUTH_BASE_URL}/oauth2/authorize?` +
      new URLSearchParams({
        client_id: CLIENT_ID,
        redirect_uri: REDIRECT_URI,
        response_type: "code",
        scope: "openid profile email",
        code_challenge: codeChallenge,
        code_challenge_method: "S256",
        state: state,
        application_id: APPLICATION_ID,
      });

    iframeRef.current = iframe;
    document.body.appendChild(iframe);
  }, []);

  let refreshTimer = null;

  function scheduleTokenRefresh(accessTokenExpiresAt) {
    try {
      const now = Date.now();
      // Refresh 60 seconds before expiry
      const refreshIn = accessTokenExpiresAt * 1000 - now - 60000;

      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }

      refreshTimer = setTimeout(
        async () => {
          await triggerSilentFlow();
        },
        Math.max(refreshIn, 0),
      );
    } catch (err) {}
  }

  return { token, loading, triggerSilentFlow };
}
